<template>
  <MapField
    v-if="showMap && position && centerPlacemark"
    :center="position"
    :placemarks="[centerPlacemark].concat(otherPlacemarks)"
    :zoom="zoom"
  />
</template>

<script setup lang="ts">
import { watchDebounced } from '@vueuse/core';

const props = defineProps<{
  centerAddress?: Address
  otherAddresses?: Address[]
  zoom: number
}>();

const showMap = ref(false);
const position = ref<google.maps.LatLng | null>();
const centerPlacemark = ref<google.maps.marker.AdvancedMarkerElement>();
const otherPlacemarks = ref<google.maps.marker.AdvancedMarkerElement[]>([]);

const { googleMapsEmbedApiKey } = useRuntimeConfig().public;
const { getCoordinatesFromAddress } = await useMaps(googleMapsEmbedApiKey);

const setMapCoordinates = async (newCoordinatesFromApi: boolean) => {
  if (!props.centerAddress?.postal_community ||
      !props.centerAddress?.postal_code ||
      !props.centerAddress?.street ||
      !props.centerAddress?.number) {
    return; // Guard against missing address elements
  }

  position.value = await getCoordinatesFromAddress(props.centerAddress, newCoordinatesFromApi);
  await nextTick();

  const infoWindow = new google.maps.InfoWindow();

  // Configure and attach the incident placemark
  const incidentPin = new google.maps.marker.PinElement({
    background: 'red',
    // glyph: // Figure out how to put a fire icon in here
  });
  const marker = new google.maps.marker.AdvancedMarkerElement({
    position: position.value,
    content: incidentPin.element,
    title: 'Incident location',
    gmpClickable: true,
  });

  marker.addListener('click', () => {
    infoWindow.close();
    infoWindow.setContent(marker.title);
    infoWindow.open(marker.map, marker);
  });

  centerPlacemark.value = marker;

  // Attach other placemarks
  const personPin = new google.maps.marker.PinElement({
    background: 'green',
    // glyph: // figure out how to put a person icon in here. e.g. mdi:account does not work in the map.
  });
  if (props.otherAddresses && props.otherAddresses.length > 0) {
    props.otherAddresses.forEach(async (address: Address) => {
      const coord = await getCoordinatesFromAddress(address, true);

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: coord,
        content: personPin.element,
        title: 'Person',
        gmpClickable: true,
      });

      marker.addListener('click', () => {
        // const { target } = domEvent;
        infoWindow.close();
        infoWindow.setContent(marker.title);
        infoWindow.open(marker.map, marker);
      });

      otherPlacemarks.value.push(marker);
    });
  }
};

onMounted(async () => {
  setMapCoordinates(!props.centerAddress?.coordinates);
  showMap.value = true;
});

watchDebounced(() => [
  props.centerAddress?.postal_code,
  props.centerAddress?.number,
  props.centerAddress?.postal_community,
  props.centerAddress?.street], () => {
  setMapCoordinates(true);
},
{ debounce: 500, maxWait: 1000 });
</script>
